import React, { useRef, useState } from 'react';
import { Upload, FileText, X, Eye, Loader2, CheckCircle2 } from 'lucide-react';
import { fileSize } from '../utils/format';
import { cn } from '../utils/cn';

// FileUpload — click-or-drag file input used by the clinic onboarding form
// and by any other single-file upload flow.
//
// Client 2026-09-10 revisions:
//   1. If the user drags MULTIPLE files at once, reject the whole drop with
//      a clear error — the previous silent "take the first one" behaviour
//      misled clinics into thinking every file uploaded successfully.
//   2. When `existing` is provided (metadata for the already-uploaded file
//      on the server), render a "View uploaded" pill so the clinic can
//      preview what they've submitted before re-uploading.
//
// Props:
//   value      — a locally-selected File object (before upload). Optional.
//   onChange   — (File | null) => void, fired when the user picks a file.
//   accept     — comma-separated accept string for the <input>.
//   maxBytes   — client-side size cap.
//   existing   — { name, size, type, uploadedAt } describing the file on
//                the server (from the profile GET), or null if none.
//   onView     — () => Promise<void>. Called when the user taps "View" on
//                the existing file. Parent is responsible for downloading
//                the blob and opening it (needs JWT header, so it can't be
//                a plain <a href>).

function FileUpload({
  value,
  onChange,
  accept = '.pdf,.jpg,.jpeg,.png,.heic,.heif,.webp',
  maxBytes,
  existing = null,
  onView = null,
}) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [error, setError] = useState('');
  const [viewBusy, setViewBusy] = useState(false);

  function pickOne(file) {
    setError('');
    if (!file) return;
    if (maxBytes && file.size > maxBytes) {
      setError(`File too large. Max ${(maxBytes / 1024 / 1024).toFixed(1)} MB.`);
      return;
    }
    onChange?.(file);
  }

  function onDrop(e) {
    e.preventDefault();
    setDrag(false);
    const list = e.dataTransfer.files;
    if (!list || list.length === 0) return;
    // Client 2026-09-10: reject multi-file drops so the clinic sees a
    // real error instead of a silent "only the first one uploaded".
    if (list.length > 1) {
      setError(`Please drop only ONE file at a time. You dropped ${list.length}.`);
      return;
    }
    pickOne(list[0]);
  }

  function onInputChange(e) {
    setError('');
    const list = e.target.files;
    if (!list || list.length === 0) return;
    if (list.length > 1) {
      setError(`Please select only ONE file at a time. You selected ${list.length}.`);
      return;
    }
    pickOne(list[0]);
  }

  async function handleView() {
    if (!onView || viewBusy) return;
    setViewBusy(true);
    try {
      await onView();
    } finally {
      setViewBusy(false);
    }
  }

  return (
    <div>
      {/* Existing-file preview strip — shown when the server already has
          a document for this field. Sits ABOVE the drop zone so the clinic
          sees it before deciding whether to re-upload. */}
      {existing && existing.name && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 10px', marginBottom: 8,
            background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 8,
          }}
        >
          <CheckCircle2 size={16} color="#065f46" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="bold" style={{
              color: '#064e3b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {existing.name}
            </div>
            <div className="text-xs muted">
              {existing.size ? fileSize(existing.size) : ''}
              {existing.uploadedAt ? ` · uploaded ${new Date(existing.uploadedAt).toLocaleDateString()}` : ''}
            </div>
          </div>
          {onView && (
            <button
              type="button"
              className="btn btn-outline"
              onClick={handleView}
              disabled={viewBusy}
              style={{ background: 'white', padding: '6px 10px', fontSize: 13 }}
            >
              {viewBusy
                ? <Loader2 size={14} style={{ animation: 'spin .8s linear infinite' }} />
                : <Eye size={14} />}
              {viewBusy ? 'Opening…' : 'View'}
            </button>
          )}
        </div>
      )}

      <button
        type="button"
        className={cn('file-drop', drag && 'is-drag')}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
      >
        <Upload size={28} strokeWidth={1.75} />
        <div className="file-drop-title">
          {existing?.name ? 'Click or drag to replace' : 'Click or drag a file to upload'}
        </div>
        <div className="file-drop-hint">
          PDF, JPG, or PNG — up to {maxBytes ? (maxBytes / 1024 / 1024).toFixed(0) : 10} MB · one file at a time
        </div>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={onInputChange}
        style={{ display: 'none' }}
      />

      {error && <div className="err" style={{ marginTop: 8 }}>{error}</div>}

      {value && (
        <div className="file-preview">
          <div className="stat-icon brand" style={{ width: 36, height: 36, background: 'var(--brand-50)', color: 'var(--brand-700)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FileText size={18} />
          </div>
          <div className="file-meta">
            <div className="bold">{value.name}</div>
            <div className="file-size">{fileSize(value.size)} · {value.type || 'file'}</div>
          </div>
          <button
            className="icon-btn"
            type="button"
            onClick={() => { onChange?.(null); if (inputRef.current) inputRef.current.value = ''; }}
            aria-label="Remove file"
          >
            <X size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

export default FileUpload;
